import type { ReactNode } from 'react';
import { useEffect, useLayoutEffect, useRef } from 'react';
import { Outlet, NavLink, useLocation } from 'react-router';
import { onMessage } from 'firebase/messaging';
import { messagingReady } from '../lib/firebase.js';
import { useNotificationBadge } from '../hooks/useNotificationBadge.js';
import { useUnreadDmCount } from '../hooks/useUnreadDmCount.js';
import { useFcmToken } from '../hooks/useFcmToken.js';
import { GeolocationProvider } from '../contexts/GeolocationContext.js';

function MapIcon() {
  return (
    <svg
      width="24"
      height="24"
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className="w-6 h-6"
    >
      <path
        d="M3 6L9 3L15 6L21 3V18L15 21L9 18L3 21V6Z"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M9 3V18"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M15 6V21"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M12 10C13.1046 10 14 9.10457 14 8C14 6.89543 13.1046 6 12 6C10.8954 6 10 6.89543 10 8C10 9.10457 10.8954 10 12 10Z"
        fill="currentColor"
      />
    </svg>
  );
}

function TrophyIcon() {
  return (
    <svg
      width="24"
      height="24"
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className="w-6 h-6"
    >
      <path
        d="M6 9H4C3.46957 9 2.96086 8.78929 2.58579 8.41421C2.21071 8.03914 2 7.53043 2 7V5H6"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M18 9H20C20.5304 9 21.0391 8.78929 21.4142 8.41421C21.7893 8.03914 22 7.53043 22 7V5H18"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M6 2H18V12C18 13.5913 17.3679 15.1174 16.2426 16.2426C15.1174 17.3679 13.5913 18 12 18C10.4087 18 8.88258 17.3679 7.75736 16.2426C6.63214 15.1174 6 13.5913 6 12V2Z"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M9 22H15"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M12 18V22"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function TimelineIcon() {
  return (
    <svg
      width="24"
      height="24"
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className="w-6 h-6"
    >
      <path
        d="M2 12H5L7 6L10 18L13 9L15 14L17 11H22"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function UserIcon() {
  return (
    <svg
      width="24"
      height="24"
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className="w-6 h-6"
    >
      <path
        d="M20 21V19C20 17.9391 19.5786 16.9217 18.8284 16.1716C18.0783 15.4214 17.0609 15 16 15H8C6.93913 15 5.92172 15.4214 5.17157 16.1716C4.42143 16.9217 4 17.9391 4 19V21"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <circle cx="12" cy="7" r="4" stroke="currentColor" strokeWidth="2" />
    </svg>
  );
}

interface NavItem {
  to: string;
  icon: ReactNode;
  label: string;
}

const NAV_ITEMS: NavItem[] = [
  { to: '/map', icon: <MapIcon />, label: 'マップ' },
  { to: '/timeline', icon: <TimelineIcon />, label: 'タイムライン' },
  { to: '/ranking', icon: <TrophyIcon />, label: 'ランキング' },
  { to: '/profile', icon: <UserIcon />, label: 'プロフィール' },
];

function NotificationBadge({ count }: { count: number }) {
  if (count <= 0) return null;
  return (
    <span className="absolute -top-1 -right-1 bg-red-500 text-white text-[10px] font-bold rounded-full min-w-[16px] h-4 flex items-center justify-center px-0.5 leading-none">
      {count > 99 ? '99+' : count}
    </span>
  );
}

export function Layout() {
  const { unreadCount, refetch } = useNotificationBadge();
  const { unreadCount: unreadDmCount, refetch: refetchDmCount } = useUnreadDmCount();
  const totalUnread = unreadCount + unreadDmCount;
  useFcmToken();
  const mainRef = useRef<HTMLElement>(null);
  const { pathname } = useLocation();

  useLayoutEffect(() => {
    if (mainRef.current) mainRef.current.scrollTop = 0;
  }, [pathname]);

  // フォアグラウンド時のFCMメッセージ処理
  // SW の onBackgroundMessage はアプリが開いているときは発火しないため、
  // onMessage でキャッチし、SW 経由で OS 通知を表示する（Android では new Notification() が使えない）
  useEffect(() => {
    let cleanup: (() => void) | undefined;
    messagingReady.then((msg) => {
      if (!msg) return;
      const unsubscribe = onMessage(msg, async (payload) => {
        const title = payload.notification?.title ?? 'RideHub';
        const body = payload.notification?.body ?? '';
        const url = payload.data?.['url'] ?? '/';
        const type = payload.data?.['type'];
        void refetch();
        void refetchDmCount();
        if (type === 'dm') {
          window.dispatchEvent(new CustomEvent('dm-received'));
        }
        if ('serviceWorker' in navigator) {
          const reg = await navigator.serviceWorker.ready;
          await reg.showNotification(title, { body, data: { url }, icon: '/pwa-192x192.png' });
        }
      });
      cleanup = unsubscribe;
    });
    return () => cleanup?.();
  }, [refetch, refetchDmCount]);

  useEffect(() => {
    const handler = () => {
      void refetchDmCount();
    };
    window.addEventListener('dm-read', handler);
    return () => window.removeEventListener('dm-read', handler);
  }, [refetchDmCount]);

  useEffect(() => {
    const handler = () => {
      void refetch();
    };
    window.addEventListener('notifications-read', handler);
    return () => window.removeEventListener('notifications-read', handler);
  }, [refetch]);

  return (
    <GeolocationProvider>
      <div className="flex flex-col h-dvh bg-stone-50">
        <main ref={mainRef} className="flex-1 min-h-0 overflow-y-auto pb-16">
          <Outlet />
        </main>
        <nav className="fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 safe-area-pb z-[2000]">
          <ul className="flex">
            {NAV_ITEMS.map((item) => {
              const isProfile = item.to === '/profile';
              return (
                <li key={item.to} className="flex-1">
                  <NavLink to={item.to} className="block">
                    {({ isActive }) => (
                      <div
                        className={`relative flex flex-col items-center gap-0.5 py-2 text-xs transition-colors ${
                          isActive ? 'text-primary' : 'text-gray-500 hover:text-gray-700'
                        }`}
                      >
                        <span
                          className={`absolute top-0 left-1/2 -translate-x-1/2 h-0.5 rounded-b-sm bg-primary transition-all duration-200 ${
                            isActive ? 'w-8' : 'w-0'
                          }`}
                        />
                        <div className="relative">
                          {item.icon}
                          {isProfile && <NotificationBadge count={totalUnread} />}
                        </div>
                        <span className={isActive ? 'font-medium' : ''}>{item.label}</span>
                      </div>
                    )}
                  </NavLink>
                </li>
              );
            })}
          </ul>
        </nav>
      </div>
    </GeolocationProvider>
  );
}
